import { Typography } from "@mui/material";
import SaveOutlined from "@mui/icons-material/SaveOutlined";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { FormTextField } from "components/Forms/FormTextField";
import { useCreateRevision } from "hooks/useCreateReport";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

type FormState = {
  reasonForReport: string;
};

export const RevisionReasonDialog = ({
  facilityId,
  reportingYear,
  onClose,
}: {
  facilityId: string;
  reportingYear: number;
  onClose: () => void;
}) => {
  const [createRevision, { loading }] = useCreateRevision();
  const navigate = useNavigate();
  const alerts = useAlerts();

  const form = useForm<FormState>({
    defaultValues: {
      reasonForReport: "",
    },
    mode: "onChange",
  });

  const { handleSubmit, watch } = form;
  const reasonForReport = watch("reasonForReport");

  const onSubmit = handleSubmit(async (data) => {
    createRevision({
      variables: {
        facilityId,
        reportingYear,
        reasonForReport: data.reasonForReport.trim(),
      },
      onCompleted(result) {
        navigate(result.createTierIIReport.id);
        onClose();
      },
      onError(err) {
        alerts.error("Error creating revision report");
        console.error("Error creating revision report", err);
        onClose();
      },
    });
  });

  // Simple client-side validation
  const trimmedReason = reasonForReport?.trim() || "";
  const isValid = trimmedReason.length > 0 && trimmedReason.length <= 400;

  return (
    <FormProvider {...form}>
      <Dialog
        open
        maxWidth="md"
        onClose={onClose}
        title="Open revision"
        primaryActionButtonProps={{
          loading,
          onClick: onSubmit,
          saveText: "SAVE AND OPEN REPORT",
          startIcon: <SaveOutlined />,
          disabled: !isValid,
        }}
      >
        <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
          Please enter a reason for revision. This will be submitted along with
          the revision in the portal where required.
        </Typography>

        <FormTextField
          name="reasonForReport"
          label="Reason for Revision *"
          fullWidth
          rules={{
            required: "Reason for revision is required",
            validate: (value: string) => {
              const trimmed = value?.trim() || "";
              if (trimmed.length > 400) {
                return "Reason for revision must be no more than 400 characters";
              }
              return true;
            },
          }}
          textFieldProps={{
            multiline: true,
            rows: 4,
            variant: "outlined",
            autoFocus: true,
          }}
        />
      </Dialog>
    </FormProvider>
  );
};
